import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError, apiFetch } from '../../data/api';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/TextField';
import { AuthLayout, FormError } from './AuthLayout';
import { COPY, validatePassword } from './authErrors';

type Phase = 'form' | 'invalid' | 'done';

/**
 * /reset/:token, opened from an admin-made reset link. The Worker has no "check this link"
 * endpoint, so the form shows straight away and learns whether the link works when it posts.
 * A successful reset signs the account out on every device (server side).
 */
export function ResetPasswordScreen() {
  const { token = '' } = useParams();
  const [phase, setPhase] = useState<Phase>('form');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const weak = validatePassword(password);
    if (weak) return setError(weak);
    if (password !== confirm) return setError(COPY.mismatch);
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/reset/${encodeURIComponent(token)}`, { json: { password } });
      setPhase('done');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_link') setPhase('invalid');
      else if (err instanceof ApiError && err.code === 'weak_password')
        setError(err.message || COPY.weak);
      else if (err instanceof ApiError) setError(COPY.generic);
      else setError(COPY.offlineTryAgain);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Set a new password">
      {phase === 'invalid' ? <p className="text-muted">{COPY.resetExpired}</p> : null}
      {phase === 'done' ? (
        <>
          <p>{COPY.resetDone}</p>
          <Link
            to="/sign-in"
            className="inline-flex min-h-11 items-center justify-center rounded-full bg-accent px-5 font-medium text-on-accent"
          >
            Sign in
          </Link>
        </>
      ) : null}
      {phase === 'form' ? (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            hint="At least 10 characters."
            maxLength={128}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <TextField
            label="Type it again"
            type="password"
            autoComplete="new-password"
            maxLength={128}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <FormError message={error} />
          <Button type="submit" variant="primary" block disabled={busy}>
            Set password
          </Button>
        </form>
      ) : null}
    </AuthLayout>
  );
}
